





























import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import {
  ContentPriceSubjectTypeEnum,
  InputContentPriceUpdate,
} from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { InputContentSubscriptionLevelUpdate } from "@scrile/api-provider/dist/api/ContentSubscriptionLevelProvider";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useMySubscriptionLevels from "@/hooks/useMySubscriptionLevels";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString } from "@/lib/moneyHelpers";
import { FormFields, FormValues } from "@/types";
import BaseDropdown from "@/components/BaseDropdown";
import BaseForm from "@/components/BaseForm";

export default defineComponent({
  name: "PostPriceSettings",
  components: { BaseDropdown, BaseForm },
  props: {
    postData: { type: Object as PropType<ContentSet>, required: true },
  },
  setup(props, { emit }) {
    const { authUserStrict, authorities } = useAuthUser();
    const { paymentPackages: packageList, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.FILESET]);
    const { mySubscriptionLevels, getMySubscriptionLevels } = useMySubscriptionLevels();
    const loading = ref(false);
    const priceList = ref<SubscriptionLevel[]>([]);
    const values = ref<FormValues>({ levelId: "0", pricePackageId: "0" });
    const isPostOwner = computed(() => props.postData.userId === authUserStrict.value.id);

    const requiredAuthorities = computed(() => {
      return isPostOwner.value
        ? ["updateMyContentPrice", "updateMyContentSubscriptionLevel"]
        : ["updateAnyContentPrice", "updateAnyContentSubscriptionLevel"];
    });

    const allowUpdate = computed(() => checkAuthorities(authorities.value, requiredAuthorities.value));
    const isFree = computed(() => values.value.levelId === "0" && values.value.pricePackageId === "0");

    const fields = computed<FormFields>(() => {
      const subscriptionList = [
        { text: isFree.value ? $t("Available for everyone") : $t("Disable access by subscription"), value: "0" },
      ].concat(
        priceList.value.map((price, index) => {
          const prices = formatMoneyString(price.pricePackage?.amount ?? 0);
          const priceString = index === priceList.value.length - 1 ? prices : $t("{0} and higher", [prices]);
          return {
            value: price.id,
            text: price.title ? price.title : priceString,
            info: price.title ? priceString : undefined,
          };
        })
      );

      const packageOptions = [{ text: $t("Disable one-time buy"), value: "0" }].concat(
        packageList.value.map(packageData => ({
          text: formatMoneyString(packageData.amount),
          value: packageData.id,
        }))
      );

      return [
        {
          name: "levelId",
          label: $t("For subscribers"),
          type: "dropdown",
          options: subscriptionList,
        },
        {
          name: "pricePackageId",
          label: $t("One-time buy"),
          type: "dropdown",
          options: packageOptions,
        },
      ];
    });

    const updatePriceData = async () => {
      if (!allowUpdate.value) return;
      const priceUpdate: InputContentPriceUpdate = {
        packageId: values.value.pricePackageId,
        subject: {
          subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
          subjectId: props.postData.id,
        },
      };
      const levelUpdate: InputContentSubscriptionLevelUpdate = {
        levelId: values.value.levelId,
        subject: {
          subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
          subjectId: props.postData.id,
        },
      };
      await Promise.all([
        providers.ContentPriceProvider.update({ data: priceUpdate }),
        providers.ContentSubscriptionLevelProvider.update({ data: levelUpdate }),
      ]);
    };

    const sendPriceEvent = () => {
      emit("priceChanged", {
        recurringLevel: priceList.value.find(i => i.id === values.value.levelId) || null,
        oneTimePackage: packageList.value.find(i => i.id === values.value.pricePackageId) || null,
      });
    };

    const onSetPriceValues = (levelId = "0", pricePackageId = "0") => {
      if (values.value.levelId === levelId && values.value.pricePackageId === pricePackageId) return;
      const priceChanged = values.value.levelId !== levelId || values.value.pricePackageId !== pricePackageId;
      values.value = {
        levelId: levelId,
        pricePackageId: pricePackageId,
      };
      if (priceChanged) {
        sendPriceEvent();
      }
    };

    const onInput = (newValues: FormValues) => onSetPriceValues(newValues.levelId, newValues.pricePackageId);

    const onInputLevelId = (newValue: string) => onSetPriceValues(newValue, values.value.pricePackageId);

    const getSubscriptionLevels = async () => {
      if (isPostOwner.value) {
        await getMySubscriptionLevels();
        priceList.value = mySubscriptionLevels.value ?? [];
      } else {
        priceList.value = await providers.SubscriptionLevelProvider.find({ userIds: [props.postData.userId] });
      }
    };

    const getPostPackageId = async () => {
      const response = await providers.ContentPriceProvider.search({
        subjects: [
          {
            subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
            subjectId: props.postData.id,
          },
        ],
      });
      return (response.find(i => i.subject.subjectId === props.postData.id) || { packageId: "0" }).packageId;
    };

    const getPostLevelId = async () => {
      const response = await providers.ContentSubscriptionLevelProvider.search({
        subjects: [
          {
            subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
            subjectId: props.postData.id,
          },
        ],
      });
      return (response.find(i => i.subject.subjectId === props.postData.id) || { levelId: "0" }).levelId;
    };

    const getData = async () => {
      if (!allowUpdate.value) return;
      try {
        loading.value = true;
        Promise.all([getPostPackageId(), getPostLevelId(), getSubscriptionLevels(), getPaymentPackages()]).then(
          ([pricePackageId, levelId]) => {
            onSetPriceValues(levelId, pricePackageId);
          }
        );
      } finally {
        loading.value = false;
      }
    };
    getData();

    emit("setUpdateCb", updatePriceData);

    return { values, fields, allowUpdate, onInput, onInputLevelId };
  },
});
